'use client'

import { useState, useRef, useMemo, useCallback } from 'react'
import type { MouseEvent } from 'react'
import { motion } from 'framer-motion'
import type { ControllerRef } from 'yet-another-react-lightbox'
import type { Slide } from 'yet-another-react-lightbox'
import BlurredLightbox from '@/components/ui/BlurredLightbox'
import LightboxImageSlide from '@/components/ui/LightboxImageSlide'
import { ChevronLeft, ChevronRight, SquareSplitHorizontal } from 'lucide-react'
import { getOptimizedImageProps } from '@inoo-ch/payload-image-optimizer/frontend'
import type { Media as MediaType } from '@/payload-types'
import type {
  InspirationGalleryItem,
  InspirationGalleryBeforeAfterSide,
} from '@/types/inspiration-gallery'
import InspirationCard from '@/components/InspirationCard/InspirationCard'
import MotionFadeIn from '@/components/ui/MotionFadeIn'
import BeforeAfterModal from '@/components/ui/BeforeAfterModal'
import LinkWithIcon from '@/components/ui/LinkWithIcon'
import Link from 'next/link'
import type { InspirationPreviewBlock } from '@/payload-types'

type LightboxSlide = {
  src: string
  alt: string
  width?: number
  height?: number
  blurDataURL?: string
  resource?: MediaType | null
  variant?: InspirationGalleryItem['media']['variant']
  before?: InspirationGalleryBeforeAfterSide & { blurDataURL?: string }
  after?: InspirationGalleryBeforeAfterSide & { blurDataURL?: string }
  defaultSplit?: number
}

interface InspirationPreviewClientProps {
  items: InspirationGalleryItem[]
  visibleCount: number
  link?: InspirationPreviewBlock['link']
}

export default function InspirationPreviewClient({
  items,
  visibleCount,
  link,
}: InspirationPreviewClientProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [lightboxSlides, setLightboxSlides] = useState<LightboxSlide[]>([])
  const [beforeAfterModalOpen, setBeforeAfterModalOpen] = useState(false)
  const [beforeAfterItem, setBeforeAfterItem] = useState<InspirationGalleryItem | null>(null)
  const [lightboxStateBeforeModal, setLightboxStateBeforeModal] = useState<{
    open: boolean
    index: number
    slides: LightboxSlide[]
  } | null>(null)
  const controllerRef = useRef<ControllerRef | null>(null)

  const visibleItems = useMemo(() => items.slice(0, visibleCount), [items, visibleCount])

  const mapItemToSlide = useCallback((item: InspirationGalleryItem): LightboxSlide => {
    const getBlur = (resource?: MediaType | null) => {
      if (!resource) return undefined
      return getOptimizedImageProps(resource as any).blurDataURL
    }

    if (item.media.variant === 'beforeAfter') {
      return {
        variant: 'beforeAfter',
        src: item.media.after?.src || '',
        alt: item.media.after?.alt || '',
        before: item.media.before
          ? { ...item.media.before, blurDataURL: getBlur(item.media.before.resource) }
          : item.media.before,
        after: item.media.after
          ? { ...item.media.after, blurDataURL: getBlur(item.media.after.resource) }
          : item.media.after,
        defaultSplit: item.media.defaultSplit || 0.5,
      }
    }
    return {
      variant: 'image',
      src: item.media.image?.src || '',
      alt: item.media.image?.alt || '',
      width: item.media.image?.width,
      height: item.media.image?.height,
      blurDataURL: getBlur(item.media.image?.resource),
      resource: item.media.image?.resource ?? null,
    }
  }, [])

  const handleLightboxOpen = useCallback(
    (item: InspirationGalleryItem) => {
      const slides = visibleItems.map(mapItemToSlide)
      const itemIndex = visibleItems.findIndex((i) => i.id === item.id)
      setLightboxSlides(slides)
      setLightboxIndex(itemIndex)
      setLightboxOpen(true)
    },
    [visibleItems, mapItemToSlide],
  )

  const handleBeforeAfterOpen = (item: InspirationGalleryItem) => {
    if (item.media.variant === 'beforeAfter') {
      setLightboxStateBeforeModal({
        open: lightboxOpen,
        index: lightboxIndex,
        slides: lightboxSlides,
      })
      setLightboxOpen(false)
      setBeforeAfterItem(item)
      setBeforeAfterModalOpen(true)
    }
  }

  return (
    <div>
      {/* Inspiration Grid */}
      <div className="mb-12">
        {visibleItems.length > 0 ? (
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {visibleItems.map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="w-full"
              >
                <MotionFadeIn>
                  <InspirationCard item={item} onGalleryOpen={handleLightboxOpen} />
                </MotionFadeIn>
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <MotionFadeIn>
            <div className="text-center py-16">
              <p className="text-text-secondary">
                Keine Inspirations-Einträge gefunden.
              </p>
            </div>
          </MotionFadeIn>
        )}
      </div>

      {/* Link to full inspiration page */}
      {link?.label && (
        <MotionFadeIn>
          <div>
            <LinkWithIcon
              link={link}
              text={link.label}
              variant="link"
              color="brand"
              size="md"
            />
          </div>
        </MotionFadeIn>
      )}

      {/* Lightbox Gallery */}
      <BlurredLightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={lightboxIndex}
        slides={lightboxSlides as unknown as Slide[]}
        carousel={{ finite: false }}
        controller={{
          ref: controllerRef,
        }}
        on={{
          view: ({ index }) => {
            setLightboxIndex(index)
          },
        }}
        styles={{
          navigationPrev: { display: 'none' },
          navigationNext: { display: 'none' },
        }}
        render={{
          controls: () => {
            if (!lightboxOpen || lightboxSlides.length <= 1) {
              return null
            }

            const handlePrev = (event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              controllerRef.current?.prev()
            }

            const handleNext = (event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation()
              controllerRef.current?.next()
            }

            return (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-between px-4 sm:px-6">
                <button
                  type="button"
                  aria-label="Vorheriges Bild"
                  onClick={handlePrev}
                  className="pointer-events-auto inline-flex h-12 w-12 items-center justify-center bg-brand text-white shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  type="button"
                  aria-label="Nächstes Bild"
                  onClick={handleNext}
                  className="pointer-events-auto inline-flex h-12 w-12 items-center justify-center bg-brand text-white shadow-lg transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </div>
            )
          },
          slide: ({ slide, rect }) => {
            const lightboxSlide = slide as LightboxSlide
            const currentItem = visibleItems[lightboxIndex]
            const isProjectImage =
              currentItem?.source === 'project' &&
              currentItem?.sourceMetadata &&
              'projectSlug' in currentItem.sourceMetadata

            if (lightboxSlide.variant === 'beforeAfter') {
              return (
                <LightboxImageSlide
                  src={lightboxSlide.after?.src || ''}
                  alt={lightboxSlide.after?.alt || ''}
                  resource={lightboxSlide.after?.resource}
                  intrinsicWidth={lightboxSlide.after?.width ?? lightboxSlide.width}
                  intrinsicHeight={lightboxSlide.after?.height ?? lightboxSlide.height}
                  rect={rect}
                  blurDataURL={lightboxSlide.after?.blurDataURL}
                  overlay={
                    <div className="absolute inset-0 z-20 flex items-center justify-center">
                      <button
                        onClick={() => {
                          const originalItem = visibleItems.find(
                            (item) =>
                              item.media.variant === 'beforeAfter' &&
                              item.media.after?.src === lightboxSlide.after?.src,
                          )
                          if (originalItem) {
                            handleBeforeAfterOpen(originalItem)
                          }
                        }}
                        className="bg-brand/90 hover:bg-brand text-white px-6 py-3 font-metropolis font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                      >
                        <div className="flex items-center gap-2">
                          <SquareSplitHorizontal size={20} />
                          VORHER / NACHHER
                        </div>
                      </button>
                    </div>
                  }
                />
              )
            }

            return (
              <LightboxImageSlide
                src={lightboxSlide.src}
                alt={lightboxSlide.alt}
                resource={lightboxSlide.resource}
                intrinsicWidth={lightboxSlide.width}
                intrinsicHeight={lightboxSlide.height}
                rect={rect}
                blurDataURL={lightboxSlide.blurDataURL}
                belowImage={
                  isProjectImage ? (
                    <Link
                      href={`/projekte/${(currentItem.sourceMetadata as any)?.projectSlug || ''}`}
                      onClick={(e) => e.stopPropagation()}
                      className="bg-brand hover:bg-brand/90 text-white px-6 py-3 font-metropolis font-medium uppercase transition-colors duration-200"
                    >
                      Projekt ansehen
                    </Link>
                  ) : null
                }
              />
            )
          },
        }}
      />

      {/* Before/After Modal */}
      {beforeAfterItem && beforeAfterItem.media.variant === 'beforeAfter' && (
        <BeforeAfterModal
          isOpen={beforeAfterModalOpen}
          onClose={() => {
            setBeforeAfterModalOpen(false)
            setBeforeAfterItem(null)
            if (lightboxStateBeforeModal?.open) {
              setLightboxSlides(lightboxStateBeforeModal.slides)
              setLightboxIndex(lightboxStateBeforeModal.index)
              setLightboxOpen(true)
            }
            setLightboxStateBeforeModal(null)
          }}
          left={{
            resource: beforeAfterItem.media.before?.resource ?? null,
            src: beforeAfterItem.media.before?.src || '',
            alt: beforeAfterItem.media.before?.alt || '',
            label: beforeAfterItem.media.before?.label || 'VORHER',
          }}
          right={{
            resource: beforeAfterItem.media.after?.resource ?? null,
            src: beforeAfterItem.media.after?.src || '',
            alt: beforeAfterItem.media.after?.alt || '',
            label: beforeAfterItem.media.after?.label || 'NACHHER',
          }}
          leftLabel={beforeAfterItem.media.before?.label ?? 'VORHER'}
          rightLabel={beforeAfterItem.media.after?.label ?? 'NACHHER'}
        />
      )}
    </div>
  )
}
